// join.yakoot.club: one public page where a merchant applies to join. The application lands in
// admin for review; admin turns it into a draft merchant and activates it once the checklist passes.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Award, BadgePercent, CheckCircle2, LocateFixed, MapPin, QrCode, Repeat, Search, Users } from 'lucide-react';
import { YakootLockup } from '@yakoot/brand';
import {
  DISCOUNTS,
  FIELD_ORDER,
  emptyForm,
  toPayload,
  validate,
  type ApplicationForm,
  type BranchCount,
  type Discount,
  type ErrorCode,
  type FieldKey,
  type Locale,
} from './application';
import { STRINGS, type Strings } from './strings';
import { PinMap } from './PinMap';
import { loadCategories, submitApplication, type CategoryOption, type SubmitResult } from './submit';

const LOCALE_KEY = 'yakoot.join.locale';

function readLocale(): Locale {
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'ar' || q === 'en') return q;
    const s = localStorage.getItem(LOCALE_KEY);
    if (s === 'ar' || s === 'en') return s;
  } catch {
    /* blocked storage: default below */
  }
  return 'ar';
}

export function JoinPage() {
  const [locale, setLocale] = useState<Locale>(readLocale);
  const s = STRINGS[locale];
  const [form, setForm] = useState<ApplicationForm>(emptyForm);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'failed'>('idle');
  const [done, setDone] = useState<SubmitResult | null>(null);
  const [categories, setCategories] = useState<CategoryOption[] | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => {
    loadCategories().then(setCategories, () => setCategories([]));
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.title = locale === 'ar' ? 'ياقوت · انضم كشريك' : 'Yakoot · Join as a partner';
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  const errors = validate(form);
  const err = (k: FieldKey) => (showErrors ? errors[k] : undefined);
  const set = <K extends FieldKey>(k: K, v: ApplicationForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      const first = FIELD_ORDER.find((k) => errors[k]);
      document.getElementById(`f-${first}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setStatus('sending');
    setRateLimited(false);
    try {
      setDone(await submitApplication(toPayload(form, locale)));
      setStatus('idle');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setRateLimited(e instanceof Error && e.message === 'rate_limited');
      setStatus('failed');
    }
  }

  return (
    <div className="min-h-full bg-[var(--join-bg)]">
      <header className="bg-[#15171A] text-white">
        <div className="mx-auto flex max-w-[1080px] items-center justify-between px-4 py-4 sm:px-6">
          <YakootLockup tone="white" markSize={38} caption={locale === 'ar' ? 'للشركاء' : 'FOR PARTNERS'} />
          <button
            type="button"
            onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
            className="rounded-pill border border-white/25 px-4 py-1.5 text-[15px] font-medium text-white/90 hover:bg-white/10"
          >
            {s.langSwitch}
          </button>
        </div>
        {!done && <Hero s={s} />}
      </header>

      <main className="mx-auto max-w-[760px] px-4 pb-16 sm:px-6">
        {done ? (
          <Done s={s} result={done} onAnother={() => { setForm(emptyForm); setShowErrors(false); setDone(null); }} />
        ) : (
          <form noValidate onSubmit={onSubmit} className="-mt-8 flex flex-col gap-5">
            <Section n={1} title={s.s1} icon={<BadgePercent className="size-5" />}>
              <Field id="nameAr" label={s.nameAr} s={s} error={err('nameAr')}>
                {(p) => <input {...p} dir="rtl" lang="ar" autoComplete="organization" value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} />}
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="categoryId" label={s.category} s={s} error={err('categoryId')}>
                  {(p) => (
                    <select {...p} value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                      <option value="" disabled>{categories === null ? s.loading : s.categoryPick}</option>
                      {(categories ?? []).map((c) => (
                        <option key={c.id} value={c.id}>{c.name[locale]}</option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field id="branchCount" label={s.branchCount} s={s}>
                  {() => (
                    <Segmented<BranchCount> name="branchCount" value={form.branchCount} onChange={(v) => set('branchCount', v)} options={(['1', '2-5', '6+'] as const).map((v) => [v, s.branches[v]])} />
                  )}
                </Field>
              </div>
              <Field id="discount" label={s.discount} s={s} error={err('discount')}>
                {() => (
                  <Segmented<Discount> name="discount" value={form.discount} onChange={(v) => set('discount', v)} options={DISCOUNTS.map((d) => [d, `${d}%`] as const)} ltr />
                )}
              </Field>
            </Section>

            <Section n={2} title={s.s2} icon={<MapPin className="size-5" />}>
              <LocationField s={s} value={form.location} onChange={(v) => set('location', v)} error={err('location')} />
            </Section>

            <Section n={3} title={s.s3} hint={s.s3Hint}>
              <Field id="contactName" label={s.contactName} s={s} error={err('contactName')}>
                {(p) => <input {...p} autoComplete="name" value={form.contactName} onChange={(e) => set('contactName', e.target.value)} />}
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="contactMobile" label={s.contactMobile} s={s} error={err('contactMobile')}>
                  {(p) => <input {...p} type="tel" dir="ltr" inputMode="tel" autoComplete="tel" placeholder="0790 123 4567" value={form.contactMobile} onChange={(e) => set('contactMobile', e.target.value)} />}
                </Field>
                <Field id="contactEmail" label={s.contactEmail} s={s} error={err('contactEmail')}>
                  {(p) => <input {...p} type="email" dir="ltr" inputMode="email" autoComplete="email" placeholder="name@example.com" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} />}
                </Field>
              </div>
            </Section>

            {/* Honeypot: off-screen and skipped by keyboard and screen readers. */}
            <div aria-hidden className="absolute -start-[9999px] h-px w-px overflow-hidden">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
              </label>
            </div>

            <div className="flex flex-col gap-4 rounded-card bg-surface-card p-5 shadow-e1 sm:p-6">
              <label id="f-consent" className="flex cursor-pointer items-start gap-3 text-[16px] leading-relaxed text-text-primary">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => set('consent', e.target.checked)}
                  aria-invalid={Boolean(err('consent'))}
                  aria-describedby={err('consent') ? 'e-consent' : undefined}
                  className="mt-1 size-5 shrink-0 accent-[#C8102E]"
                />
                <span>{s.consent}</span>
              </label>
              <TermsDialog s={s} />
              {err('consent') && <p id="e-consent" className="text-[14px] text-status-danger">{s.errors.consent}</p>}
              {showErrors && Object.keys(errors).length > 0 && (
                <p role="alert" className="rounded-input bg-status-tint-danger px-4 py-3 text-[15px] text-status-danger">{s.fixErrors}</p>
              )}
              {status === 'failed' && <p role="alert" className="rounded-input bg-status-tint-danger px-4 py-3 text-[15px] text-status-danger">{rateLimited ? s.rateLimited : s.submitFailed}</p>}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="h-[54px] rounded-button bg-[#C8102E] text-[17px] font-semibold text-white shadow-[0_8px_20px_rgba(200,16,46,0.28)] transition hover:bg-[#A50D26] disabled:opacity-60"
              >
                {status === 'sending' ? s.submitting : s.submit}
              </button>
            </div>
          </form>
        )}
      </main>

      <footer className="pb-10 text-center text-[14px] text-text-secondary">{s.footer}</footer>
    </div>
  );
}

const VALUE_ICONS = [Users, Award, QrCode, Repeat];

function Hero({ s }: { s: Strings }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
      <p className="text-[14px] font-semibold uppercase tracking-micro text-[#E7A3AE] rtl:tracking-normal">{s.kicker}</p>
      <h1 className="mt-2 text-[32px] font-light leading-tight sm:text-[42px]">{s.title}</h1>
      <p className="mt-3 max-w-[600px] text-[17px] leading-relaxed text-white/75">{s.lead}</p>
      <h2 className="mt-8 text-[19px] font-semibold text-white">{s.whyTitle}</h2>
      <ul className="mt-3 grid gap-3 sm:grid-cols-2">
        {s.values.map(([title, body], i) => {
          const Icon = VALUE_ICONS[i] ?? BadgePercent;
          return (
            <li key={title} className="flex items-start gap-3 rounded-[14px] bg-white/[0.06] p-4">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#C8102E]/20 text-[#E7A3AE]">
                <Icon className="size-[18px]" />
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-[16px] font-semibold text-white">{title}</span>
                <span className="text-[14px] leading-relaxed text-white/70">{body}</span>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-5 text-[14px] text-white/55">{s.steps}</p>
    </div>
  );
}

function Section({ n, title, hint, icon, children }: { n: number; title: string; hint?: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-5 rounded-card bg-surface-card p-5 shadow-e1 sm:p-7" aria-labelledby={`s-${n}`}>
      <div className="flex items-start gap-3">
        <span className="tst-num flex size-8 shrink-0 items-center justify-center rounded-full bg-[#15171A] text-[15px] text-white">{n}</span>
        <div className="flex flex-col gap-1">
          <h2 id={`s-${n}`} className="flex items-center gap-2 text-[21px] font-semibold text-text-primary">
            {title}
            {icon && <span className="text-text-secondary">{icon}</span>}
          </h2>
          {hint && <p className="text-[15px] text-text-secondary">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

const inputClass =
  'w-full rounded-input border bg-surface-card px-4 py-3 text-[17px] text-text-primary placeholder:text-text-secondary/60 focus:border-text-primary focus:outline-none';

interface ControlProps {
  id: string;
  className: string;
  'aria-invalid': boolean;
  'aria-describedby'?: string;
}

function Field({ id, label, s, error, counter, children }: { id: FieldKey; label: string; s: Strings; error?: ErrorCode; counter?: string; children: (p: ControlProps) => ReactNode }) {
  const describedBy = error ? `e-${id}` : undefined;
  return (
    <div id={`f-${id}`} className="flex flex-col gap-1.5">
      <label htmlFor={`i-${id}`} className="text-[16px] font-medium text-text-primary">
        {label}
      </label>
      {children({ id: `i-${id}`, className: `${inputClass} ${error ? 'border-status-danger' : 'border-border-subtle'}`, 'aria-invalid': Boolean(error), 'aria-describedby': describedBy })}
      {(error || counter) && (
        <div className="flex justify-between gap-3">
          <p id={`e-${id}`} className="text-[14px] text-status-danger">{error ? s.errors[error] : ''}</p>
          {counter && <span className="tst-num shrink-0 text-[13px] text-text-secondary">{counter}</span>}
        </div>
      )}
    </div>
  );
}

function Segmented<T extends string | number>({ name, value, onChange, options, ltr }: { name: string; value: T | null; onChange: (v: T) => void; options: (readonly [T, string])[]; ltr?: boolean }) {
  return (
    <div role="radiogroup" id={`i-${name}`} className="grid auto-cols-fr grid-flow-col gap-2">
      {options.map(([v, label]) => (
        <label
          key={v}
          className={`flex cursor-pointer items-center justify-center rounded-input border px-2 py-3 text-center text-[15px] transition ${value === v ? 'border-[#15171A] bg-[#15171A] font-semibold text-white' : 'border-border-subtle bg-surface-card text-text-primary hover:border-text-secondary'}`}
        >
          <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} className="sr-only" />
          {ltr ? <span className="tst-num">{label}</span> : label}
        </label>
      ))}
    </div>
  );
}

function LocationField({ s, value, onChange, error }: { s: Strings; value: ApplicationForm['location']; onChange: (v: ApplicationForm['location']) => void; error?: ErrorCode }) {
  const [locating, setLocating] = useState<'idle' | 'busy' | 'failed'>('idle');
  const hintId = useId();
  const locate = () => {
    if (!navigator.geolocation) return setLocating('failed');
    setLocating('busy');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating('idle');
        onChange({ lat: Math.round(pos.coords.latitude * 1e6) / 1e6, lng: Math.round(pos.coords.longitude * 1e6) / 1e6 });
      },
      () => setLocating('failed'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const wrapper = useRef<HTMLDivElement>(null);
  return (
    <div id="f-location" ref={wrapper} className="flex flex-col gap-2">
      <div className="text-[16px] font-medium text-text-primary">{s.mapLabel}</div>
      <p id={hintId} className="text-[14px] text-text-secondary">{s.mapHint}</p>
      <PlaceSearch s={s} onPick={onChange} />
      <PinMap value={value} onChange={onChange} invalid={Boolean(error)} label={s.mapLabel} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={locate} disabled={locating === 'busy'} className="inline-flex items-center gap-2 rounded-pill border border-border-subtle px-4 py-2 text-[15px] font-medium text-text-primary hover:bg-surface-tint disabled:opacity-60">
          <LocateFixed className="size-4" />
          {locating === 'busy' ? s.locating : s.myLocation}
        </button>
        {value && (
          <span className="inline-flex items-center gap-2 text-[14px] text-status-success">
            <CheckCircle2 className="size-4" />
            {s.pinSet} · <span className="tst-num text-text-secondary">{value.lat.toFixed(5)}, {value.lng.toFixed(5)}</span>
            <button type="button" onClick={() => onChange(null)} className="text-text-secondary underline underline-offset-2 hover:text-text-primary">{s.clearPin}</button>
          </span>
        )}
      </div>
      {locating === 'failed' && <p className="text-[14px] text-status-warning">{s.locateFailed}</p>}
      {error && <p className="text-[14px] text-status-danger">{s.errors.location}</p>}
    </div>
  );
}

function Done({ s, result, onAnother }: { s: Strings; result: SubmitResult; onAnother: () => void }) {
  return (
    <div className="mt-8 flex flex-col items-center gap-4 rounded-card bg-surface-card px-6 py-10 text-center shadow-e1">
      <CheckCircle2 className="size-14 text-status-success" strokeWidth={1.6} />
      <h1 className="text-[28px] font-light text-text-primary">{s.doneTitle}</h1>
      <p className="max-w-[480px] text-[17px] leading-relaxed text-text-secondary">{s.doneBody}</p>
      <div className="rounded-[14px] bg-surface-tint px-5 py-3">
        <span className="text-[14px] text-text-secondary">{s.reference}</span>
        <div className="tst-num text-[24px] text-text-primary">{result.reference}</div>
      </div>
      <p className="max-w-[480px] text-[15px] text-text-secondary">{s.doneNext}</p>
      {result.simulated && <p className="rounded-input bg-status-tint-warning px-4 py-2 text-[14px] text-status-warning">{s.localNote}</p>}
      <button type="button" onClick={onAnother} className="mt-2 text-[15px] font-medium text-text-primary underline underline-offset-4">{s.another}</button>
    </div>
  );
}

interface Place {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

/**
 * Address search through OpenStreetMap's Nominatim (no key). Runs only on Enter or the button, never
 * per keystroke, to stay within its usage policy (max 1 request a second). Results are limited to Iraq.
 */
function PlaceSearch({ s, onPick }: { s: Strings; onPick: (p: { lat: number; lng: number }) => void }) {
  const [q, setQ] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'empty' | 'failed'>('idle');
  const [results, setResults] = useState<Place[]>([]);
  const lang = document.documentElement.lang || 'ar';

  async function run() {
    const query = q.trim();
    if (query.length < 2 || state === 'busy') return;
    setState('busy');
    setResults([]);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=iq&accept-language=${lang}&q=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const items = (await res.json()) as Place[];
      setResults(items);
      setState(items.length ? 'idle' : 'empty');
    } catch {
      setState('failed');
    }
  }

  const pick = (p: Place) => {
    onPick({ lat: Math.round(Number(p.lat) * 1e6) / 1e6, lng: Math.round(Number(p.lon) * 1e6) / 1e6 });
    setResults([]);
    setQ(p.display_name.split(',')[0] ?? q);
  };

  return (
    <div className="relative flex flex-col gap-1.5">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute start-3.5 top-1/2 size-[18px] -translate-y-1/2 text-text-secondary" aria-hidden />
          <input
            type="search"
            enterKeyHint="search"
            aria-label={s.search}
            placeholder={s.searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void run();
              }
            }}
            className={`${inputClass} border-border-subtle ps-10`}
          />
        </div>
        <button type="button" onClick={() => void run()} disabled={state === 'busy'} className="shrink-0 rounded-input bg-[#15171A] px-5 text-[16px] font-semibold text-white hover:bg-black disabled:opacity-60">
          {state === 'busy' ? s.searching : s.search}
        </button>
      </div>
      {results.length > 0 && (
        <ul role="listbox" aria-label={s.search} className="overflow-hidden rounded-input border border-border-subtle bg-surface-card shadow-e2">
          {results.map((r) => (
            <li key={r.place_id} role="option" aria-selected={false}>
              <button type="button" onClick={() => pick(r)} className="flex w-full items-start gap-2.5 px-4 py-3 text-start text-[15px] leading-snug text-text-primary hover:bg-surface-tint">
                <MapPin className="mt-0.5 size-4 shrink-0 text-[#C8102E]" />
                <span>{r.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {state === 'empty' && <p className="text-[14px] text-text-secondary">{s.noResults}</p>}
      {state === 'failed' && <p className="text-[14px] text-status-warning">{s.searchFailed}</p>}
    </div>
  );
}

function TermsDialog({ s }: { s: Strings }) {
  const ref = useRef<HTMLDialogElement>(null);
  return (
    <>
      <button type="button" onClick={() => ref.current?.showModal()} className="-mt-2 self-start ps-8 text-[15px] font-medium text-[#C8102E] underline underline-offset-4 hover:text-[#A50D26]">
        {s.viewTerms}
      </button>
      <dialog
        ref={ref}
        aria-labelledby="terms-title"
        onClick={(e) => e.target === ref.current && ref.current?.close()}
        className="w-[min(560px,calc(100vw-32px))] rounded-card bg-surface-card p-0 text-text-primary shadow-e3 backdrop:bg-black/50"
      >
        <div className="flex flex-col gap-4 p-6">
          <h2 id="terms-title" className="text-[21px] font-semibold">{s.termsTitle}</h2>
          <p className="text-[16px] leading-relaxed text-text-secondary">{s.terms}</p>
          <button type="button" onClick={() => ref.current?.close()} className="h-[48px] rounded-button bg-[#15171A] text-[16px] font-semibold text-white hover:bg-black">
            {s.close}
          </button>
        </div>
      </dialog>
    </>
  );
}
